import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import type { PitchStrategy } from '@/features/sales/types';
import { useAutomationUserContext } from './useAutomationUserContext';
import { getErrorMessage } from '@/utils/error';

export interface SxmPitch {
  id: number;
  orgId: number;
  targetId?: number;
  target?: {
    id: number;
    name: string;
    role?: string;
    industry?: string;
  };
  strategy?: PitchStrategy;
  version: number;
  active: boolean;
  createdBy?: string;
  createdAt: Date;
}

export interface CreateSxmPitchDto {
  targetId?: number;
  strategy?: PitchStrategy;
  version?: number;
  active?: boolean;
}

export interface UpdateSxmPitchDto {
  targetId?: number;
  strategy?: PitchStrategy;
  version?: number;
  active?: boolean;
}

export const useSxmPitches = (enabled: boolean = false) => {
  const { user, targetUserId, isInternalUser } = useAutomationUserContext();
  const [pitches, setPitches] = useState<SxmPitch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPitches = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = isInternalUser && targetUserId !== user?.id ? '/sxm/internal/pitches' : '/sxm/pitches';
      const { data } = await api.get(endpoint, { params: { userId: targetUserId } });
      setPitches(data);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [targetUserId, enabled, isInternalUser, user?.id]);

  const createPitch = async (dto: CreateSxmPitchDto): Promise<SxmPitch> => {
    try {
      const { data: newPitch } = await api.post('/sxm/pitches', dto, { params: { userId: targetUserId } });
      if (enabled) {
        await fetchPitches();
      } else {
        setPitches(prev => [...prev, newPitch]);
      }
      return newPitch;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const updatePitch = async (id: number, dto: UpdateSxmPitchDto): Promise<SxmPitch> => {
    try {
      const { data: updatedPitch } = await api.patch(`/sxm/pitches/${id}`, dto, { params: { userId: targetUserId } });
      setPitches(prev => prev.map(pitch => pitch.id === id ? updatedPitch : pitch));
      return updatedPitch;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const deletePitch = async (id: number): Promise<void> => {
    try {
      await api.delete(`/sxm/pitches/${id}`, { params: { userId: targetUserId } });
      setPitches(prev => prev.filter(pitch => pitch.id !== id));
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchPitches();
    }
  }, [enabled, fetchPitches]);

  return {
    pitches,
    loading,
    error,
    createPitch,
    updatePitch,
    deletePitch,
    refetch: fetchPitches,
  };
};
